// © 2015-2021 WDNA - All Rights Reserved

import api, { AxiosRequestConfig } from '../../../services/api/Connect';

export * from './types';

/**
 * Executes and endpoint operation based on params
 *
 * @param action action to perform on endpoint
 * @param id identifier if necessary
 * @param data request body to send
 * @param options request options to send
 * @returns the response if is success or error otherwise
 */
export function sensor<T = any>(action: string, id?: string, data?: any, options?: AxiosRequestConfig): Promise<T> | undefined {
  const root: string = 'sensor';
  switch (action) {
    case 'list':
      return api.post(`${root}/list`, data, options);
    case 'get':
      return id ? api.get(`${root}/get/${id}`, data, options) : undefined;
    case 'set':
      return api.put(`${root}/set`, data, options);
    case 'del':
      return id ? api.delete(`${root}/del/${id}`, data, options) : undefined;
    case 'update':
      return api.put(`${root}/update`, data, options);
    case 'alerts':
      return api.get(`${root}/alerts`, data, options);
    case 'statistics':
      return id ? api.get(`${root}/statistics/${id}`, data, options) : undefined;
    case 'copy':
      return api.get(`${root}/register/copy`, data, options);
    case 'register_get':
      return id ? api.get(`${root}/register/get/${id}/${data.timestamp}`, data, options) : undefined;
    case 'register_list':
      return id ? api.get(`${root}/register/list/${id}`, data, options) : undefined;
    case 'register_graph':
      return id ? api.get(`${root}/register/graph/${id}`, data, options) : undefined;
    case 'register_lasts':
      return api.get(`${root}/register/lasts`, data, options);
    case 'gis_position':
      return api.post(`${root}/gis/position`, data, options);
    case 'gis_tracking':
      return id ? api.get(`${root}/gis/tracking/${id}`, data, options) : undefined;
    case 'chirpstack_set':
      return id ? api.get(`${root}/chirpstack/queue/set/${id}`, data, options) : undefined;
    case 'chirpstack_del':
      return id ? api.delete(`${root}/chirpstack/queue/del/${id}`, data, options) : undefined;
    default:
      return undefined;
  }
}

/**
 * Executes and endpoint operation based on params
 *
 * @param action action to perform on endpoint
 * @param id identifier if necessary
 * @param data request body to send
 * @param options request options to send
 * @returns the response if is success or error otherwise
 */
export function sensorAdjustment<T = any>(action: string, id?: string, data?: any, options?: AxiosRequestConfig): Promise<T> | undefined {
  const root: string = 'sensor/adjustment';
  switch (action) {
    case 'list':
      return api.post(`${root}/list`, data, options);
    case 'get':
      return id ? api.get(`${root}/get/${id}`, data, options) : undefined;
    case 'set':
      return api.put(`${root}/set`, data, options);
    case 'del':
      return id ? api.delete(`${root}/del/${id}`, data, options) : undefined;
    default:
      return undefined;
  }
}

/**
 * Executes and endpoint operation based on params
 *
 * @param action action to perform on endpoint
 * @param id identifier if necessary
 * @param data request body to send
 * @param options request options to send
 * @returns the response if is success or error otherwise
 */
export function waste<T = any>(action: string, id?: string, data?: any, options?: AxiosRequestConfig): Promise<T> | undefined {
  const root: string = 'waste';
  switch (action) {
    case 'list':
      return api.post(`${root}/list`, data, options);
    case 'get':
      return id ? api.get(`${root}/get/${id}`, data, options) : undefined;
    case 'set':
      return api.put(`${root}/set`, data, options);
    case 'del':
      return id ? api.delete(`${root}/del/${id}`, data, options) : undefined;
    case 'info_list':
      return id ? api.get(`${root}/info/list/${id}`, data, options) : undefined;
    case 'info_get':
      return (id && data.date) ? api.get(`${root}/info/get/${id}/${data.date.toISOString()}`, data, options) : undefined;
    case 'info_set':
      return api.put(`${root}/info/set`, data, options);
    case 'info_del':
      return (id && data.date) ? api.delete(`${root}/info/del/${id}/${data.date.toISOString()}`, data, options) : undefined;
    case 'unload_list':
      return api.get(`${root}/unload/list`, data, options);
    case 'unload_graph':
      return api.get(`${root}/unload/graph`, data, options);
    case 'unload_reads':
      return api.get(`${root}/unload/reads`, data, options);
    case 'unload_verify':
      return id ? api.get(`${root}/unload/verify/${id}`, data, options) : undefined;
    case 'dashboard':
      return api.get(`${root}/dashboard`, data, options);
    case 'statistics':
      return api.get(`${root}/statistics`, data, options);
    case 'weights':
      return api.get(`${root}/weights`, data, options);
    default:
      return undefined;
  }
}

/**
 * Executes and endpoint operation based on params
 *
 * @param action action to perform on endpoint
 * @param id identifier if necessary
 * @param data request body to send
 * @param options request options to send
 * @returns the response if is success or error otherwise
 */
export function weather<T = any>(action: string, id: string, data?: any, options?: AxiosRequestConfig): Promise<T> | undefined {
  const root: string = 'weather';
  switch (action) {
    case 'statistics':
      return api.get(`${root}/statistics/${id}`, data, options);
    case 'windrose':
      return api.get(`${root}/windrose/${id}`, data, options);
    default:
      return undefined;
  }
}
